import { appRoutes } from "@/app/router/appRoutes";
import { api } from "@/modules/api";
import type { ApiSchema } from "@/modules/api";

export interface FeatureEnrollmentLinkSummary {
  id: string;
  featureFlagKey: string;
  slug: string;
  title: string;
  description: string | null;
  redirectPath: string | null;
}

export interface FeatureEnrollmentSummary {
  id: string;
  featureEnrollmentLinkId: string;
  spaceAccountId: string;
  acceptedByAccountId: string;
  featureFlagKey: string;
  status: string;
}

export type FeatureEnrollmentLookupResult =
  | { kind: "ready"; link: FeatureEnrollmentLinkSummary }
  | {
      kind: "already-enrolled";
      link: FeatureEnrollmentLinkSummary;
      enrollment: FeatureEnrollmentSummary;
    }
  | { kind: "expired"; link?: FeatureEnrollmentLinkSummary; message?: string }
  | { kind: "disabled"; link?: FeatureEnrollmentLinkSummary; message?: string }
  | { kind: "not-found"; message?: string }
  | { kind: "error"; message: string };

export type AcceptFeatureEnrollmentResult =
  | {
      kind: "accepted";
      link: FeatureEnrollmentLinkSummary;
      enrollment: FeatureEnrollmentSummary;
    }
  | { kind: "expired"; message?: string }
  | { kind: "disabled"; message?: string }
  | { kind: "not-found"; message?: string }
  | { kind: "error"; message: string };

type FeatureEnrollmentLinkResponse = ApiSchema["FeatureEnrollmentLinkResponseSchema"];
type FeatureEnrollmentResponse = ApiSchema["FeatureEnrollmentResponseSchema"];
type VerifyFeatureEnrollmentLinkResponse = ApiSchema["VerifyFeatureEnrollmentLinkResponse"];

export const getNormalizedFeatureEnrollmentSlug = (slug: string | undefined): string =>
  slug?.trim() ?? "";

export const resolveFeatureEnrollmentRedirectPath = (
  redirectPath: string | null | undefined
): string => {
  const normalizedRedirectPath = redirectPath?.trim();

  if (!normalizedRedirectPath) {
    return appRoutes.notesList({}).path;
  }

  if (!normalizedRedirectPath.startsWith("/") || normalizedRedirectPath.startsWith("//")) {
    return appRoutes.notesList({}).path;
  }

  return normalizedRedirectPath;
};

const extractErrorDetail = (error: unknown): string | undefined => {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  if ("detail" in error && typeof (error as { detail?: string }).detail === "string") {
    return (error as { detail?: string }).detail;
  }

  return undefined;
};

const mapFeatureEnrollmentLink = (
  link: FeatureEnrollmentLinkResponse
): FeatureEnrollmentLinkSummary => ({
  id: link.id,
  featureFlagKey: link.feature_flag_key,
  slug: link.slug,
  title: link.title,
  description: link.description ?? null,
  redirectPath: link.redirect_path ?? null,
});

const mapFeatureEnrollment = (enrollment: FeatureEnrollmentResponse): FeatureEnrollmentSummary => ({
  id: enrollment.id,
  featureEnrollmentLinkId: enrollment.feature_enrollment_link_id,
  spaceAccountId: enrollment.space_account_id,
  acceptedByAccountId: enrollment.accepted_by_account_id,
  featureFlagKey: enrollment.feature_flag_key,
  status: enrollment.status,
});

const mapVerificationResponse = (
  response: VerifyFeatureEnrollmentLinkResponse
): FeatureEnrollmentLookupResult => {
  const link = mapFeatureEnrollmentLink(response.feature_enrollment_link);
  const enrollment = response.feature_enrollment
    ? mapFeatureEnrollment(response.feature_enrollment)
    : null;

  if (response.is_disabled || response.feature_enrollment_link.is_disabled) {
    return { kind: "disabled", link };
  }

  if (response.is_expired) {
    return { kind: "expired", link };
  }

  if (enrollment && enrollment.status.toUpperCase() === "ACTIVE") {
    return { kind: "already-enrolled", link, enrollment };
  }

  return { kind: "ready", link };
};

const mapActionError = ({
  status,
  detail,
  fallbackMessage,
}: {
  status: number;
  detail?: string;
  fallbackMessage: string;
}): Exclude<AcceptFeatureEnrollmentResult, { kind: "accepted" }> => {
  if (status === 404 || detail === "Feature enrollment link not found.") {
    return { kind: "not-found", message: detail };
  }

  if (detail === "Feature enrollment link has expired.") {
    return { kind: "expired", message: detail };
  }

  if (
    detail === "Feature enrollment link is disabled." ||
    detail === "Feature enrollment link has reached its maximum enrollment count."
  ) {
    return { kind: "disabled", message: detail };
  }

  if (status === 401) {
    return { kind: "error", message: "Please sign in to enable this feature." };
  }

  return { kind: "error", message: detail ?? fallbackMessage };
};

export const fetchFeatureEnrollmentLinkBySlug = async (
  slug: string | undefined
): Promise<FeatureEnrollmentLookupResult> => {
  const normalizedSlug = getNormalizedFeatureEnrollmentSlug(slug);

  if (!normalizedSlug) {
    return { kind: "not-found" };
  }

  try {
    const response = await api.post("/v2/feature-enrollment-links/{slug}/verify", {
      params: {
        path: {
          slug: normalizedSlug,
        },
      },
    });

    if (!response.data || response.error) {
      const status = response.response?.status ?? 0;
      const detail = extractErrorDetail(response.error);

      if (status === 404 || detail === "Feature enrollment link not found.") {
        return { kind: "not-found", message: detail };
      }

      return {
        kind: "error",
        message: detail ?? "We couldn't verify this feature enrollment link.",
      };
    }

    return mapVerificationResponse(response.data);
  } catch (_error) {
    return { kind: "error", message: "We couldn't verify this feature enrollment link." };
  }
};

export const acceptFeatureEnrollmentLinkBySlug = async (
  slug: string | undefined
): Promise<AcceptFeatureEnrollmentResult> => {
  const normalizedSlug = getNormalizedFeatureEnrollmentSlug(slug);

  if (!normalizedSlug) {
    return { kind: "not-found" };
  }

  try {
    const response = await api.post("/v2/feature-enrollment-links/{slug}/accept", {
      params: {
        path: {
          slug: normalizedSlug,
        },
      },
    });

    if (!response.data || response.error) {
      const status = response.response?.status ?? 0;
      const detail = extractErrorDetail(response.error);

      return mapActionError({
        status,
        detail,
        fallbackMessage: "Something went wrong. Please try again.",
      });
    }

    return {
      kind: "accepted",
      link: mapFeatureEnrollmentLink(response.data.feature_enrollment_link),
      enrollment: mapFeatureEnrollment(response.data.feature_enrollment),
    };
  } catch (error) {
    return {
      kind: "error",
      message: (error as Error).message ?? "Something went wrong. Please try again.",
    };
  }
};
